/**
 * Excel-aktig ↑/↓ mellom tilbudslinjer i samme kolonne.
 * VIB-1000: piltaster skal aldri endre tallverdien — de flytter fokus.
 * ←/→ rører ikke denne helperen (enhetsvelgeren sykler selv).
 */

export const OFFER_NAV_ATTR = "data-offer-nav";

/** Felles grid slik at kolonneheader og rader holder samme spor. */
export const OFFER_LINE_GRID =
  "grid-cols-[20px_1fr_52px_92px_72px_64px_96px_44px]";

export type OfferNavDir = "up" | "down";

export type OfferUnitHotkey = "t" | "d" | "stk" | "fast";

const UNIT_HOTKEYS: Record<string, OfferUnitHotkey> = {
  t: "t",
  d: "d",
  s: "stk",
  f: "fast",
};

/** t/d/s/f → enhet, eller null hvis tasten ikke gjelder (eller enheten ikke er tillatt). */
export function unitFromHotkey(
  key: string,
  allowed: readonly string[],
): OfferUnitHotkey | null {
  if (key.length !== 1) return null;
  const unit = UNIT_HOTKEYS[key.toLowerCase()];
  if (!unit || !allowed.includes(unit)) return null;
  return unit;
}

/** `o` åpner overtidslinjen, samme mønster som t/d/s/f. */
export function isOvertimeHotkey(key: string): boolean {
  return key.length === 1 && key.toLowerCase() === "o";
}

function navCellOf(from: HTMLElement): HTMLElement | null {
  return from.closest<HTMLElement>(`[${OFFER_NAV_ATTR}]`);
}

export function focusOfferNavCell(el: HTMLElement): void {
  const target =
    el.getAttribute("role") === "radiogroup"
      ? (el.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ?? el)
      : el;
  target.focus();
  if (target instanceof HTMLInputElement) target.select();
}

/** Flytt fokus til samme kolonne på nablinja. Returnerer treffet, eller null. */
export function moveOfferNav(from: HTMLElement, dir: OfferNavDir): HTMLElement | null {
  const cell = navCellOf(from);
  if (!cell) return null;
  const col = cell.getAttribute(OFFER_NAV_ATTR);
  if (!col) return null;
  const root = from.closest<HTMLElement>("[data-offer-table]") ?? document.body;
  const cells = Array.from(
    root.querySelectorAll<HTMLElement>(`[${OFFER_NAV_ATTR}="${col}"]`),
  );
  const idx = cells.indexOf(cell);
  if (idx < 0) return null;
  const next = dir === "down" ? cells[idx + 1] : cells[idx - 1];
  if (!next) return null;
  focusOfferNavCell(next);
  return next;
}

/**
 * preventDefault + flytt. True når ↑/↓ ble konsumert (også uten nabocelle),
 * slik at number-inputs aldri stepper verdien (VIB-1000).
 */
export function handleOfferArrowNav(
  e: Pick<KeyboardEvent, "key" | "altKey" | "metaKey" | "ctrlKey"> & {
    preventDefault: () => void;
    currentTarget: EventTarget;
  },
): boolean {
  if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return false;
  if (e.altKey || e.metaKey || e.ctrlKey) return false;
  e.preventDefault();
  moveOfferNav(e.currentTarget as HTMLElement, e.key === "ArrowDown" ? "down" : "up");
  return true;
}

export type OfferDragDropKind = "none" | "move" | "copy";

/**
 * Option/Alt-dra = kopi, ellers flytt. Tom source eller slipp på samme
 * rad uten Alt er no-op. Safari rapporterer ofte ikke altKey under HTML5-drag.
 */
export function classifyOfferDragDrop(
  sourceId: string | null,
  targetId: string,
  altKey: boolean,
): OfferDragDropKind {
  if (!sourceId) return "none";
  if (altKey) return "copy";
  if (sourceId === targetId) return "none";
  return "move";
}

/** Linje-id for elementet som har fokus (celle inne i raden). */
export function focusedOfferLineId(
  from: EventTarget | null = typeof document === "undefined" ? null : document.activeElement,
): string | null {
  const el = from as HTMLElement | null;
  return el?.closest?.("[data-line-id]")?.getAttribute("data-line-id") ?? null;
}
